import type { ReactNode } from 'react';

import { DietTagIcons } from './DietTagIcons';

const NBSP = '\u00A0';

/**
 * Splits `value` at its last word boundary. Internal whitespace runs
 * (including non-ASCII whitespace like NBSP or tabs from a pasted name) are
 * first collapsed to a single space so a stray non-space separator can't
 * strand more than one word in `trailing`.
 */
function splitFinalWord(value: string): { leading: string; trailing: string } {
  const normalized = value.trim().replace(/\s+/gu, ' ');
  const lastSpace = normalized.lastIndexOf(' ');
  if (lastSpace === -1) return { leading: '', trailing: normalized };

  return {
    leading: normalized.slice(0, lastSpace),
    trailing: normalized.slice(lastSpace + 1),
  };
}

export interface NameWithDietIconsProps {
  name: string;
  dietTags: string[] | undefined;
  chefRecommended?: boolean;
  /** Wraps the whole name in a `<span>` carrying this class. Omit to render bare text (no wrapper) when there's nothing to bind. */
  className?: string;
  /**
   * Extra inline content (e.g. a format badge) appended after the icon
   * cluster, inside an inline-flex box shared with the final word/icons.
   * Being an atomic inline box (not a flex sibling of the whole, possibly
   * multi-line, name) lets it tuck onto the last line when there's room, or
   * wrap down as a unit when there isn't; the gap to it is flex gap rather
   * than a margin, so it collapses to flush-left when it wraps alone.
   */
  trailingInline?: ReactNode;
}

/**
 * Renders `name` with the diet-tag/chef-recommended icon cluster (when
 * present) bound to the final word, so the icons can't separate from it.
 * Without icons, the last two words are further joined with a non-breaking
 * space so the final word alone can't become a widow on the last line — a
 * join skipped when icons are present, since they already give that word
 * enough visual weight to not read as an orphan.
 */
export function NameWithDietIcons({
  name,
  dietTags,
  chefRecommended = false,
  className,
  trailingInline,
}: NameWithDietIconsProps) {
  const hasIndicators = Boolean((dietTags && dietTags.length > 0) || chefRecommended);
  const iconCluster = hasIndicators && (
    <span
      className="ml-2 inline-flex shrink-0 items-center gap-1 whitespace-nowrap align-middle"
      data-testid="diet-icons"
    >
      <DietTagIcons tags={dietTags} chefRecommended={chefRecommended} />
    </span>
  );

  const { leading, trailing } = splitFinalWord(name);
  // inline-block keeps the icons on the final word's line but lets an overlong word wrap.
  const trailingWord = iconCluster ? (
    <span className="inline-block max-w-full">
      {trailing}
      {iconCluster}
    </span>
  ) : (
    <span>{trailing}</span>
  );
  const trailingUnit = trailingInline ? (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 align-middle">
      {trailingWord}
      {trailingInline}
    </span>
  ) : (
    trailingWord
  );

  const hasTrailingWeight = Boolean(iconCluster || trailingInline);

  let content;
  if (!leading) {
    content = hasTrailingWeight ? trailingUnit : trailing;
  } else if (hasTrailingWeight) {
    // The icon cluster / badge gives the trailing word enough visual weight
    // that it never reads as a bare orphan on its own line, so the preceding
    // word is left free to wrap normally instead of being dragged down to
    // join it.
    content = (
      <>
        {leading + ' '}
        {trailingUnit}
      </>
    );
  } else {
    // Nothing to lean on — join the last two words with a non-breaking
    // space so the final word can't wrap alone onto its own line.
    content = (
      <>
        {leading + NBSP}
        {trailingUnit}
      </>
    );
  }

  return className ? <span className={className}>{content}</span> : <>{content}</>;
}
